import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import type { Resource } from '../../../resources';

import { getAllSuggestedPrompts, pickRandomPrompts } from './suggestedPrompts';

const DISPLAY_COUNT = 3;
const ROTATE_INTERVAL_MS = 4000;
const EXIT_ANIMATION_MS = 300;

interface RotatingPrompt {
  text: string;
  key: number;
}

/**
 * Returns 3 visible prompts and rotates one out at a time on a timer.
 * Each prompt has a stable `key` for React transitions, and `exitingKey`
 * identifies the prompt currently animating out.
 */
export function useRotatingPrompts(resource?: Resource, enabled = true, rand?: () => number) {
  const allPrompts = getAllSuggestedPrompts(resource);
  const keyCounter = useRef(0);
  const rotationIndex = useRef(0);
  const [visiblePrompts, setVisiblePrompts] = useState<RotatingPrompt[]>([]);
  const [exitingKey, setExitingKey] = useState<number | null>(null);
  const visiblePromptsRef = useLatestRef(visiblePrompts);

  const nextKey = useCallback(() => {
    keyCounter.current += 1;
    return keyCounter.current;
  }, []);

  // Initialize with random prompts when resource changes
  useEffect(() => {
    if (allPrompts.length === 0) {
      setVisiblePrompts([]);
      return;
    }
    keyCounter.current = 0;
    rotationIndex.current = 0;
    const initial = pickRandomPrompts(allPrompts, DISPLAY_COUNT, new Set(), rand);
    setVisiblePrompts(initial.map((text) => ({ text, key: nextKey() })));
  }, [resource]); // oxlint-disable-line react-hooks/exhaustive-deps

  // Rotate one prompt at a time
  useEffect(() => {
    if (!enabled || rand !== undefined || allPrompts.length <= DISPLAY_COUNT || visiblePrompts.length === 0) {
      return;
    }

    let pendingTimeout: ReturnType<typeof setTimeout> | null = null;

    const timer = setInterval(() => {
      const slot = rotationIndex.current % DISPLAY_COUNT;
      rotationIndex.current += 1;

      // Phase 1: mark the slot as exiting (read from ref to avoid stale closure)
      const current = visiblePromptsRef.current;
      if (current[slot]) {
        setExitingKey(current[slot].key);
      }

      // Phase 2: after the exit animation, swap in a new prompt
      pendingTimeout = setTimeout(() => {
        pendingTimeout = null;
        setVisiblePrompts((prev) => {
          const currentTexts = new Set(prev.map((p) => p.text));
          const [newText] = pickRandomPrompts(allPrompts, 1, currentTexts);
          if (!newText) {
            return prev;
          }

          const updated = [...prev];
          updated[slot] = { text: newText, key: nextKey() };
          return updated;
        });
        setExitingKey(null);
      }, EXIT_ANIMATION_MS);
    }, ROTATE_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
      }
    };
  }, [allPrompts, visiblePrompts.length, nextKey, enabled, rand]);

  return { visiblePrompts, exitingKey };
}
